import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { products as pApi, preorderApi } from "@/lib/api";
import { toast } from "sonner";
import Confetti from "./Confetti";
import { ShoppingBag, Plus, Minus, Clock, X, Check } from "lucide-react";

export default function PreOrderModal({ shop, onClose }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // {productId: qty}
  const [pickup, setPickup] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  });
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confetti, setConfetti] = useState(false);

  useEffect(() => {
    if (!shop) return;
    pApi.list({ dispensary_id: shop.id, sort: "value", limit: 30 }).then(setProducts);
  }, [shop]);

  const cartItems = Object.entries(cart).map(([id, qty]) => {
    const p = products.find((x) => x.id === id);
    return p ? { id, name: p.name, qty, price: p.price } : null;
  }).filter(Boolean);
  const total = cartItems.reduce((s, i) => s + i.qty * i.price, 0);

  const setQty = (id, delta) => {
    setCart((c) => {
      const n = (c[id] || 0) + delta;
      const next = { ...c };
      if (n <= 0) delete next[id];
      else next[id] = Math.min(n, 10);
      return next;
    });
  };

  const submit = async () => {
    if (!user) { onClose?.(); nav("/auth?mode=login"); return; }
    if (cartItems.length === 0) { toast.error("Pick at least one item"); return; }
    setBusy(true);
    try {
      const r = await preorderApi.create({
        dispensary_id: shop.id,
        items: cartItems.map((i) => ({ name: i.name, qty: i.qty, price: i.price })),
        pickup_time: pickup,
        notes,
      });
      setDone(r);
      setConfetti(true);
      setTimeout(() => setConfetti(false), 1800);
      refresh();
      toast.success("Pre-order locked in · +15 XP");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place pre-order");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!shop} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl rounded-3xl border-2 border-[#0B0F0C] bg-[#FAF6E8] [&>button]:hidden p-0 overflow-hidden shadow-[10px_10px_0_#15FF73] relative max-h-[90vh] flex flex-col" data-testid="preorder-modal">
        {confetti && <Confetti />}
        <div className="bg-[#15FF73] border-b-2 border-[#0B0F0C] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono-x text-[10px] font-bold uppercase tracking-widest">Pre-order · {shop?.city}, {shop?.state}</div>
            <div className="font-display text-2xl font-extrabold leading-tight">{shop?.name}</div>
          </div>
          <button onClick={onClose} className="text-[#0B0F0C]"><X size={20} strokeWidth={2.6} /></button>
        </div>

        {!done ? (
          <>
          <div className="p-6 overflow-auto flex-1">
            <div className="label-overline text-[#FF1A8C] mb-3">Pick your items</div>
            <div className="space-y-2 mb-5">
              {products.length === 0 && <div className="text-sm text-[#0B0F0C]/55">Loading menu…</div>}
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] p-3">
                  <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded-xl border-2 border-[#0B0F0C]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold leading-tight truncate">{p.name}</div>
                    <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#0B0F0C]/55">{p.strain} · {p.thc}% THC</div>
                  </div>
                  <div className="font-display font-extrabold text-lg w-14 text-right">${p.price.toFixed(0)}</div>
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`qty-minus-${p.id}`} onClick={() => setQty(p.id, -1)} className="w-7 h-7 rounded-full border-2 border-[#0B0F0C] hover:bg-[#FF1A8C] hover:text-white flex items-center justify-center"><Minus size={11} strokeWidth={2.6} /></button>
                    <div className="w-7 text-center font-mono-x font-bold">{cart[p.id] || 0}</div>
                    <button data-testid={`qty-plus-${p.id}`} onClick={() => setQty(p.id, 1)} className="w-7 h-7 rounded-full border-2 border-[#0B0F0C] hover:bg-[#15FF73] flex items-center justify-center"><Plus size={11} strokeWidth={2.6} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <label className="flex items-center gap-3 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] px-4 h-12">
                <Clock size={14} strokeWidth={2.4} className="text-[#FF1A8C]" />
                <input data-testid="pickup-time" type="datetime-local" value={pickup} onChange={(e) => setPickup(e.target.value)} className="bg-transparent flex-1 outline-none text-sm font-mono-x font-bold" />
              </label>
              <input data-testid="preorder-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] focus:bg-white focus:shadow-[3px_3px_0_#0B0F0C] px-4 h-12 text-sm placeholder:text-[#0B0F0C]/40 outline-none transition-all" />
            </div>
          </div>
          <div className="border-t-2 border-[#0B0F0C] bg-[#FFE74C]/40 px-6 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="label-overline text-[#0B0F0C]/55">Subtotal · {cartItems.reduce((s, i) => s + i.qty, 0)} items</div>
              <div className="font-display text-3xl font-extrabold">${total.toFixed(2)}</div>
            </div>
            <button data-testid="preorder-submit" disabled={busy || cartItems.length === 0} onClick={submit} className="btn-x btn-primary disabled:opacity-50">
              <ShoppingBag size={14} strokeWidth={2.4} /> Lock it in (+15 XP)
            </button>
          </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-[#15FF73] border-2 border-[#0B0F0C] shadow-[4px_4px_0_#0B0F0C] flex items-center justify-center mb-4">
              <Check size={32} strokeWidth={3} />
            </div>
            <h3 className="font-display text-4xl font-extrabold leading-tight mb-2">Reserved!</h3>
            <div className="font-mono-x text-2xl font-bold tracking-widest bg-[#0B0F0C] text-[#15FF73] rounded-full px-4 py-3 inline-block mt-3 mb-5">{done.code}</div>
            <p className="text-sm text-[#0B0F0C]/65 max-w-sm mx-auto leading-relaxed mb-6">Show your code at the counter within 4 hours. We'll have your order ready.</p>
            <button onClick={() => { onClose?.(); nav("/me"); }} className="btn-x btn-primary">See my orders →</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
